import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { realLifeCopy, realLifeData } from "../config/media";

const EASE = [0.16, 1, 0.3, 1];

export const RealLifeSection = () => {
  const [activeId, setActiveId] = useState(realLifeData[0].id);
  const active = realLifeData.find((s) => s.id === activeId);

  return (
    <section
      data-testid="real-life-section"
      className="px-6 lg:px-10 py-14 lg:py-20"
    >
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-120px" }}
        transition={{ duration: 0.8, ease: EASE }}
        className="flex items-end justify-between gap-12"
      >
        <div className="max-w-[620px]">
          <p
            data-testid="real-life-eyebrow"
            className="text-xs font-semibold uppercase tracking-[0.22em] text-[var(--strollee-accent)]"
          >
            {realLifeCopy.eyebrow}
          </p>
          <h2 className="mt-4 text-[36px] lg:text-[48px] font-semibold leading-[1.1] tracking-[-0.03em] text-[var(--strollee-ink)]">
            {realLifeCopy.headlineLines.map((line) => (
              <span key={line} className="block">
                {line}
              </span>
            ))}
          </h2>
        </div>
        <p className="max-w-[340px] pb-2 text-base leading-relaxed text-[var(--strollee-muted)]">
          {realLifeCopy.support}
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: EASE, delay: 0.12 }}
        className="mt-8 lg:mt-10"
      >
        <div className="relative h-[calc(90vh-76px)] lg:h-[calc(90vh-80px)] overflow-hidden rounded-[var(--strollee-radius-hero)] bg-[var(--strollee-line)]/40">
          {realLifeData.map((s) => (
            <motion.img
              key={s.id}
              src={s.image.url}
              alt={s.image.alt}
              data-testid={s.id === activeId ? "real-life-media-active" : `real-life-media-${s.id}`}
              initial={false}
              animate={{
                opacity: s.id === activeId ? 1 : 0,
                scale: s.id === activeId ? 1 : 1.03,
              }}
              transition={{ duration: 0.7, ease: EASE }}
              className="absolute inset-0 h-full w-full object-cover"
            />
          ))}
          <div className="absolute inset-0 bg-gradient-to-t from-[#171713]/70 via-[#171713]/10 to-[#171713]/25" />

          <div
            role="tablist"
            aria-label="Real-life chapters"
            data-testid="real-life-tablist"
            className="absolute left-6 top-6 lg:left-8 lg:top-8 inline-flex items-center gap-1 rounded-full border border-white/25 bg-white/10 p-1.5 backdrop-blur-md"
          >
            {realLifeData.map((s) => {
              const isActive = s.id === activeId;
              return (
                <button
                  key={s.id}
                  role="tab"
                  aria-selected={isActive}
                  data-testid={`real-life-tab-${s.id}`}
                  onClick={() => setActiveId(s.id)}
                  className={`relative rounded-full px-5 py-2.5 text-[13px] transition-colors duration-200 ${
                    isActive
                      ? "font-semibold text-[var(--strollee-ink)]"
                      : "font-medium text-white/75 hover:text-white"
                  }`}
                >
                  {isActive && (
                    <motion.span
                      layoutId="real-life-active-pill"
                      transition={{ duration: 0.4, ease: EASE }}
                      className="absolute inset-0 rounded-full bg-[var(--strollee-surface)]"
                    />
                  )}
                  <span className="relative z-10">{s.label}</span>
                </button>
              );
            })}
          </div>

          <div className="absolute inset-x-0 bottom-0 p-8 lg:p-14">
            <AnimatePresence mode="wait">
              <motion.div
                key={active.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.45, ease: EASE }}
                className="max-w-[560px]"
              >
                <h3
                  data-testid="real-life-title"
                  className="text-[32px] lg:text-[44px] font-semibold leading-[1.08] tracking-[-0.025em] text-white"
                >
                  {active.title}
                </h3>
                <p
                  data-testid="real-life-copy"
                  className="mt-3 max-w-[480px] text-base lg:text-lg leading-relaxed text-white/80"
                >
                  {active.copy}
                </p>
                <div className="mt-7">
                  <a
                    href="#s1"
                    data-testid="real-life-cta"
                    className="group inline-flex items-center gap-2 rounded-full bg-[var(--strollee-surface)] px-6 py-3 text-sm font-semibold tracking-wide text-[var(--strollee-ink)] transition-all duration-200 hover:bg-white hover:shadow-[0_8px_28px_rgba(0,0,0,0.25)] hover:-translate-y-[2px]"
                  >
                    {realLifeCopy.cta}
                    <ArrowUpRight
                      size={16}
                      strokeWidth={2.2}
                      className="transition-transform duration-200 group-hover:translate-x-[2px] group-hover:-translate-y-[2px]"
                    />
                  </a>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </motion.div>
    </section>
  );
};
